import React from "react";
import { FastField, Field } from "formik";
import { Output } from "../../Text";
import { Fieldset, Form, Layout } from "@vwfs-bronson/bronson-react";
import {
  Conditional,
  FieldDependent,
  renderRadioGroup,
  SelectField,
  TextField,
} from "../../formik";
import * as Validation from "../../validation";
import {
  showContractType,
  showEmployedSince,
  showEmployerFields,
} from "../../functional/employment";
import toOptions from "../../functional/toOptions";
import { FormSection2 } from "./FormSection";
import { DateField } from "../../formik/DateField";
import { resetField } from "../../formik/resetField";
import { alphabeticallyValueCompare } from '../../utils';
import { defaultSelectValue } from '../../common/types/common';

const select = (whitelist, all) => all.filter((v) => whitelist.includes(v.key));

function EmploymentSection({
  closed,
  success,
  showEditButton,
  onIconClick,
  form,
  buttons,
  containerRef,
}) {
  const orderedProfessionGroups = Output.resolve(
    "pages.applicationForm.panels.employmentData.fields.professionGroup.values",
    []
  )?.sort(alphabeticallyValueCompare);

  return (
    <FormSection2
      containerRef={containerRef}
      title={Output.resolve(
        "pages.applicationForm.panels.employmentData.title"
      )}
      subtitle={Output.resolve(
        "pages.applicationForm.panels.employmentData.subtitle"
      )}
      success={success}
      expanded={!closed}
      onHeaderClick={onIconClick}
      edit={
        !showEditButton
          ? undefined
          : {
              label: Output.resolve("pages.applicationForm.buttonEdit"),
              onClick: onIconClick,
            }
      }
    >
      <Form {...form} floatingLabel>
        <Fieldset>
          <Fieldset.Row>
            <Layout>
              <Layout.Item default={"6/12"} m={"1/2"} xs={"1/1"}>
                <FastField
                  name="professionGroup"
                  label={Output.resolve(
                    "pages.applicationForm.panels.employmentData.fields.professionGroup.label"
                  )}
                  component={SelectField}
                >
                  {toOptions(
                    [
                      defaultSelectValue,
                      ...orderedProfessionGroups,
                    ]
                  )}
                </FastField>
              </Layout.Item>
              <Layout.Item default={"6/12"} m={"1/2"} xs={"1/1"}>
                <FieldDependent
                  names={["professionGroup"]}
                  render={({ values, formik }) => (
                    <Conditional
                      collapsed={!showContractType(values.professionGroup)}
                      onCollapse={() => {
                        resetField("employment", formik);
                      }}
                    >
                      {renderRadioGroup({
                        fieldName: "employment",
                        legend: Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.employment.label"
                        ),
                        options: select(
                          ["limited", "unlimited"],
                          Output.resolve(
                            "pages.applicationForm.panels.employmentData.fields.employment.values"
                          )
                        ),
                      })}
                    </Conditional>
                  )}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
          <FieldDependent
            names={["professionGroup"]}
            render={({ values: { professionGroup }, formik }) => (
              <Conditional
                collapsed={!showEmployerFields(professionGroup)}
                onCollapse={() => {
                  resetField("employerName", formik);
                  resetField("employerLocation", formik);
                }}
              >
                <Fieldset.Row>
                  <Layout>
                    <Layout.Item default="6/12" m="1/2" xs="1/1">
                      <FastField
                        name="employerName"
                        label={Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.employerName.label"
                        )}
                        tooltip={Output.resolveAsMarkdown(
                          "pages.applicationForm.panels.employmentData.fields.employerName.toolTip"
                        )}
                        maxLength="35"
                        component={TextField}
                      />
                    </Layout.Item>
                    <Layout.Item default="6/12" m="1/2" xs="1/1">
                      <FastField
                        name="employerLocation"
                        label={Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.employerLocation.label"
                        )}
                        tooltip={Output.resolveAsMarkdown(
                          "pages.applicationForm.panels.employmentData.fields.employerLocation.toolTip"
                        )}
                        maxLength="50"
                        component={TextField}
                      />
                    </Layout.Item>
                  </Layout>
                </Fieldset.Row>
              </Conditional>
            )}
          />
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FieldDependent
                  names={["professionGroup"]}
                  render={({ values: { professionGroup }, formik }) => (
                    <Conditional
                      collapsed={!showEmployedSince(professionGroup)}
                      onCollapse={() => {
                        resetField("employedSince", formik);
                      }}
                    >
                      <Field
                        name="employedSince"
                        label={Output.resolve(`pages.applicationForm.panels.employmentData.fields.employedSince.${
                          professionGroup === 'PENSIONER' ? 'labelBis' : 'label'
                        }`)}
                        component={DateField}
                      />
                    </Conditional>
                  )}
                />
              </Layout.Item>
              <Layout.Item default="6/12" m="1/2" xs="1/1">
                <FieldDependent
                  names={["employment", "professionGroup"]}
                  render={({
                    values: { employment, professionGroup },
                    formik,
                  }) => (
                    <Conditional
                      collapsed={employment !== "limited"}
                      onCollapse={() => {
                        resetField("employmentLimit", formik);
                      }}
                    >
                      <FastField
                        name="employmentLimit"
                        label={Output.resolve(
                          "pages.applicationForm.panels.employmentData.fields.employmentPeriod.label"
                        )}
                        component={DateField}
                      />
                    </Conditional>
                  )}
                />
              </Layout.Item>
            </Layout>
          </Fieldset.Row>
        </Fieldset>
        <Fieldset>
          <Fieldset.Row>{buttons}</Fieldset.Row>
        </Fieldset>
      </Form>
    </FormSection2>
  );
}

EmploymentSection.defaultProps = {
  validate: Validation.select(
    "professionGroup",
    "employment",
    /*'business',*/
    "employerName",
    "employerLocation",
    "employedSince",
    "employmentLimit"
  ),
};

export default EmploymentSection;
